import axios from 'axios';
import { logError, logDebug } from '../utils/logger';

// Set the base URL for your API
// Force direct connection to local backend to bypass proxy
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001/api';

// Create an Axios instance for API requests that require form data (e.g., file uploads)
const apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'multipart/form-data',
    },
});

// Create a standard Axios instance for JSON requests
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Export the api instance for use in other services
export { api, apiClient };

/**
 * Convert Firebase Storage URLs to direct proxy URLs for better performance
 * @param {string|Array} images - Image URL(s) to convert
 * @returns {string|Array} - Proxy URL(s)
 */
const convertToProxyUrls = (images) => {
    if (!images) return images;
    
    if (Array.isArray(images)) {
        return images.map(url => {
            if (typeof url === 'string' && url.includes('storage.googleapis.com/forked-prod.firebasestorage.app/images/')) {
                // Extract the filename from the Firebase Storage URL
                const filename = url.split('/images/')[1];
                return `${API_BASE_URL}/upload/proxy/${filename}`;
            }
            return url;
        });
    }
    
    if (typeof images === 'string' && images.includes('storage.googleapis.com/forked-prod.firebasestorage.app/images/')) {
        // Extract the filename from the Firebase Storage URL
        const filename = images.split('/images/')[1];
        return `${API_BASE_URL}/upload/proxy/${filename}`;
    }
    
    return images;
};

/**
 * Create a new recipe (now saves to stack)
 * @param {FormData} data - FormData object containing recipe fields and images
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Created recipe data
 */
export const createRecipe = async (data, token) => {
    try {
        const response = await apiClient.post('/recipes', data, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        logDebug('[api.createRecipe]', 'Recipe created successfully', { recipeId: response.data?.id });
        return response.data;
    } catch (error) {
        logError('[api.createRecipe]', 'Error creating recipe', error.response?.data || error);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Update an existing recipe (in stack)
 * @param {string} id - ID of the recipe to update
 * @param {FormData} data - FormData object containing updated fields and images
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Updated recipe data
 */
export const updateRecipe = async (id, data, token) => {
    try {
        const response = await apiClient.put(`/recipes/${id}`, data, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error updating recipe:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Update recipe privacy setting
 * @param {string} id - ID of the recipe
 * @param {boolean} isPublic - Whether the recipe should be public
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Updated recipe data
 */
export const updateRecipePrivacy = async (id, isPublic, token) => {
    try {
        const response = await apiClient.put(`/recipes/${id}/privacy`, { is_public: isPublic }, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error updating recipe privacy:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Fork a recipe (from stack)
 * @param {string} id - ID of the recipe to fork
 * @param {FormData} data - FormData object containing fork details and images
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Forked recipe data
 */
export const forkRecipe = async (id, data, token) => {
    try {
        const response = await apiClient.post(`/recipes/${id}/fork`, data, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        logDebug('[api.forkRecipe]', 'Recipe forked successfully', { forkId: response.data?.id, sourceId: id });
        return response.data;
    } catch (error) {
        logError('[api.forkRecipe]', 'Error forking recipe', error.response?.data || error);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Import a recipe from a URL (now saves to stack)
 * @param {string} url - URL of the recipe to import
 * @param {string} baseId - Optional base ID to associate with
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Imported recipe data
 */
export const importRecipeFromUrl = async (url, baseId, token) => {
    try {
        // Handle backwards compatibility - if baseId is actually the token
        let actualBaseId = null;
        let actualToken = token;
        
        if (typeof baseId === 'string' && !token) {
            // Old signature: (url, token)
            actualToken = baseId;
            actualBaseId = null;
        } else {
            // New signature: (url, baseId, token)
            actualBaseId = baseId;
            actualToken = token;
        }
        
        const response = await api.post('/recipes/import-url', { url, baseId: actualBaseId }, {
            headers: {
                Authorization: `Bearer ${actualToken}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error importing recipe from URL:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Get a recipe by ID (from stack)
 * @param {string} id - ID of the recipe to fetch
 * @returns {Promise<Object>} - Recipe data
 */
export const getRecipeById = async (id) => {
    try {
        // Get auth token if user is logged in (allows viewing private recipes)
        const headers = {};
        try {
            const { auth } = require('../firebase/firebaseConfig');
            const user = auth.currentUser;
            if (user) {
                const token = await user.getIdToken();
                headers.Authorization = `Bearer ${token}`;
            }
        } catch (authError) {
            // If auth is not available or user is not logged in, continue without token
            // This allows public recipes to still be viewable
        }
        
        const response = await api.get(`/recipes/${id}`, { headers });
        // Convert Firebase Storage URLs to proxy URLs for better accessibility
        const data = {
            ...response.data,
            images: convertToProxyUrls(response.data.images)
        };
        return data;
    } catch (error) {
        // Don't log 403 errors for private recipes when user is not authenticated - this is expected
        let shouldLog = true;
        if (error.response?.status === 403) {
            try {
                const { auth } = require('../firebase/firebaseConfig');
                if (!auth.currentUser) {
                    shouldLog = false; // Expected behavior - user needs to log in
                }
            } catch {
                // Auth not available, log the error
            }
        }
        
        if (shouldLog) {
            console.error('❌ Error fetching recipe by ID:', error.response?.data?.message || error.message);
        }
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Get fork lineage for a recipe
 * @param {string} id - ID of the recipe to get lineage for
 * @returns {Promise<Object>} - Fork lineage data with parent, children, and all recipes in chain
 */
export const getRecipeLineage = async (id) => {
    try {
        const response = await api.get(`/recipes/${id}/lineage`);
        // Convert Firebase Storage URLs to proxy URLs for better accessibility
        const data = {
            ...response.data,
            recipes: response.data.recipes.map(recipe => ({
                ...recipe,
                images: convertToProxyUrls(recipe.images)
            }))
        };
        return data;
    } catch (error) {
        console.error('❌ Error fetching recipe lineage:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Get all recipes (from stack)
 * @returns {Promise<Array>} - Array of all recipes
 */
export const getAllRecipes = async () => {
    try {
        const response = await api.get('/recipes');
        // Convert Firebase Storage URLs to proxy URLs for better accessibility
        const data = response.data.map(recipe => ({
            ...recipe,
            images: convertToProxyUrls(recipe.images)
        }));
        return data;
    } catch (error) {
        console.error('❌ Error fetching all recipes:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Get all recipes for a specific user (from stack)
 * @param {string} userId - ID of the user
 * @param {string} token - Authorization token
 * @returns {Promise<Array>} - Array of user's recipes
 */
export const getUserRecipes = async (userId, token) => {
    try {
        const response = await api.get(`/users/${userId}/recipes`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        // Convert Firebase Storage URLs to proxy URLs for better accessibility
        const data = response.data.map(recipe => ({
            ...recipe,
            images: convertToProxyUrls(recipe.images)
        }));
        return data;
    } catch (error) {
        console.error('❌ Error fetching user recipes:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Delete a recipe (from stack)
 * @param {string} id - ID of the recipe to delete
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Deletion confirmation
 */
export const deleteRecipe = async (id, token) => {
    try {
        const response = await apiClient.delete(`/recipes/${id}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error deleting recipe:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Get the profile of the logged-in user
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - User profile data
 */
export const getUserProfile = async (token) => {
    try {
        const response = await api.get('/users/me', {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error fetching user profile:', error.response?.data?.message || error.message);
        // Preserve the error code from the backend response
        const errorData = error.response?.data || { message: error.message };
        if (error.response?.status === 403 && errorData.code === 'USER_NOT_REGISTERED') {
            errorData.code = 'USER_NOT_REGISTERED';
        }
        throw errorData;
    }
};

/**
 * Register a new user in the database
 * @param {Object} userData - User registration data
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Registration result
 */
export const registerUser = async (userData, token) => {
    try {
        const response = await api.post('/users/register', userData, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        
        return response.data;
    } catch (error) {
        console.error('❌ API: User registration failed', error.response?.data || error);
        
        // Provide specific error information
        const errorMessage = error.response?.data?.message || error.message || 'Database registration failed';
        const errorDetails = {
            message: errorMessage,
            status: error.response?.status,
            code: error.response?.data?.code
        };
        
        // Throw a more descriptive error
        const enhancedError = new Error(`Database registration failed: ${errorMessage}`);
        enhancedError.details = errorDetails;
        throw enhancedError;
    }
};

/**
 * Get a user by ID
 * @param {string} userId - ID of the user to fetch
 * @returns {Promise<Object>} - User data
 */
export const getUserById = async (userId) => {
    try {
        const response = await api.get(`/users/${userId}`);
        return response.data;
    } catch (error) {
        console.error('❌ Error fetching user by ID:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Update user profile
 * @param {string} userId - ID of the user to update
 * @param {Object} profileData - Profile data to update
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Updated user data
 */
export const updateUserProfile = async (userId, profileData, token) => {
    try {
        // Send all the profile data including new fields
        const body = { 
            display_name: profileData.display_name || profileData.displayName || '',
            is_public: profileData.is_public || false,
            background_info: profileData.background_info || {},
            session_preferences: profileData.session_preferences || {},
            agent_name: profileData.agent_name,
            agent_trigger: profileData.agent_trigger,
            agent_enabled: profileData.agent_enabled,
            agent_preferences: profileData.agent_preferences || {}
        };
        
        const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(body),
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Server error');
        }
        return await response.json();
    } catch (error) {
        console.error('❌ Error updating user profile:', error);
        throw error;
    }
};

/**
 * Get user's planning theme preference
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Theme preference (theme: 'tomato' | 'lemon' | 'spinach')
 */
export const getPlanningTheme = async (token) => {
    try {
        const response = await fetch(`${API_BASE_URL}/users/planning-theme`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Server error');
        }

        return await response.json();
    } catch (error) {
        console.error('❌ Error getting planning theme:', error);
        throw error;
    }
};

/**
 * Update user's planning theme preference
 * @param {string} theme - Theme ('tomato' | 'lemon' | 'spinach')
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Updated theme preference
 */
export const updatePlanningTheme = async (theme, token) => {
    try {
        const response = await fetch(`${API_BASE_URL}/users/planning-theme`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ theme })
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Server error');
        }

        return await response.json();
    } catch (error) {
        console.error('❌ Error updating planning theme:', error);
        throw error;
    }
};

/**
 * Update user agent settings specifically
 * @param {Object} agentData - Agent settings data
 * @param {string} agentData.agent_name - Agent name
 * @param {string} agentData.agent_trigger - Agent trigger phrase
 * @param {boolean} agentData.agent_enabled - Whether agent is enabled
 * @param {Object} agentData.agent_preferences - Agent preferences
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Updated agent settings
 */
export const updateAgentSettings = async (agentData, token) => {
    try {
        const response = await api.put('/users/agent', agentData, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error updating agent settings:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Health Check
 * @returns {Promise<Object>} - Backend health status
 */
export const healthCheck = async () => {
    try {
        const response = await api.get('/health');
        return response.data;
    } catch (error) {
        console.error('❌ Error during health check:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Generate recipe with AI context using both user input and URL data
 * @param {Object} data - Object containing user recipe data and URL
 * @param {string} token - Authorization token
 * @param {Function} onProgress - Callback function for progress updates
 * @returns {Promise<Object>} - Generated recipe data
 */
export const generateRecipeWithAI = async (data, token, onProgress = null) => {
    try {
        console.log('🚀 Starting AI recipe generation...');
        console.log('📤 Request data:', data);
        
        const response = await fetch(`${api.defaults.baseURL}/recipes/generate-with-context`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });

        console.log('📥 Response status:', response.status);
        console.log('📥 Response headers:', Object.fromEntries(response.headers.entries()));

        if (!response.ok) {
            const errorData = await response.json();
            console.error('❌ Response not ok:', errorData);
            throw new Error(errorData.error || 'Failed to generate recipe');
        }

        const contentType = response.headers.get('content-type');
        console.log('📥 Content-Type:', contentType);

        // Check if it's SSE or regular JSON response
        if (contentType && contentType.includes('text/event-stream')) {
            
            const reader = response.body.getReader();
            const decoder = new TextDecoder();

            return new Promise((resolve, reject) => {
                const processStream = async () => {
                    try {
                        while (true) {
                            const { done, value } = await reader.read();
                            
                            if (done) {
                                break;
                            }

                            const chunk = decoder.decode(value);
                            console.log('📨 Received chunk:', chunk);
                            
                            const lines = chunk.split('\n');

                            for (const line of lines) {
                                if (line.startsWith('data: ')) {
                                    try {
                                        const parsedData = JSON.parse(line.slice(6));
                                        
                                        if (parsedData.step === 'result') {
                                            resolve(parsedData);
                                            return;
                                        } else if (parsedData.step === 'error') {
                                            console.error('❌ Error received:', parsedData.message);
                                            reject(new Error(parsedData.message));
                                            return;
                                        } else if (onProgress) {
                                            console.log('📈 Progress update:', parsedData);
                                            onProgress(parsedData);
                                        }
                                    } catch (parseError) {
                                        console.warn('⚠️ Failed to parse SSE data:', parseError);
                                    }
                                }
                            }
                        }
                    } catch (error) {
                        console.error('❌ Stream processing error:', error);
                        reject(error);
                    }
                };

                processStream();
            });
        } else {
            console.log('📄 Regular JSON response detected, processing as fallback...');
            
            // Fallback: Handle as regular JSON response with simulated progress
            const jsonData = await response.json();
            
            // Simulate progress updates for better UX
            if (onProgress) {
                onProgress({ step: 'scraping', message: 'Analyzing recipe URL...', progress: 10 });
                await new Promise(resolve => setTimeout(resolve, 500));
                
                onProgress({ step: 'scraping', message: 'Recipe data extracted successfully', progress: 30 });
                await new Promise(resolve => setTimeout(resolve, 500));
                
                onProgress({ step: 'processing', message: 'Processing your recipe information...', progress: 50 });
                await new Promise(resolve => setTimeout(resolve, 500));
                
                onProgress({ step: 'generating', message: 'Combining recipes with AI...', progress: 70 });
                await new Promise(resolve => setTimeout(resolve, 500));
                
                onProgress({ step: 'generating', message: 'Recipe enhancement completed', progress: 90 });
                await new Promise(resolve => setTimeout(resolve, 500));
                
                onProgress({ step: 'complete', message: 'Recipe transfer complete!', progress: 100 });
                await new Promise(resolve => setTimeout(resolve, 500));
            }
            
            // Return the data in the expected format
            return {
                step: 'result',
                recipe: jsonData,
                sourceUrl: data.url
            };
        }
    } catch (error) {
        console.error('❌ Error generating recipe with AI:', error.message);
        throw error;
    }
};

/**
 * Generate recipe with AI using pasted text content (no URL scraping)
 * @param {Object} data - Object containing user recipe data and text content
 * @param {string} token - Authorization token
 * @param {Function} onProgress - Callback function for progress updates
 * @returns {Promise<Object>} - Generated recipe data
 */
export const generateRecipeWithText = async (data, token, onProgress = null) => {
    try {
        console.log('🚀 Starting AI recipe generation from text...');
        console.log('📤 Request data:', { ...data, textContent: `${data.textContent?.substring(0, 100)}...` });
        
        const response = await fetch(`${api.defaults.baseURL}/recipes/generate-with-text`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });

        console.log('📥 Response status:', response.status);

        if (!response.ok) {
            const errorData = await response.json();
            console.error('❌ Response not ok:', errorData);
            throw new Error(errorData.error || 'Failed to generate recipe from text');
        }

        const contentType = response.headers.get('content-type');
        console.log('📥 Content-Type:', contentType);

        // Check if it's SSE or regular JSON response
        if (contentType && contentType.includes('text/event-stream')) {
            
            const reader = response.body.getReader();
            const decoder = new TextDecoder();

            return new Promise((resolve, reject) => {
                const processStream = async () => {
                    try {
                        while (true) {
                            const { done, value } = await reader.read();
                            
                            if (done) {
                                break;
                            }

                            const chunk = decoder.decode(value);
                            console.log('📨 Received chunk:', chunk);
                            
                            const lines = chunk.split('\n');

                            for (const line of lines) {
                                if (line.startsWith('data: ')) {
                                    try {
                                        const parsedData = JSON.parse(line.slice(6));
                                        
                                        if (parsedData.step === 'result') {
                                            resolve(parsedData);
                                            return;
                                        } else if (parsedData.step === 'error') {
                                            console.error('❌ Error received:', parsedData.message);
                                            reject(new Error(parsedData.message));
                                            return;
                                        } else if (onProgress) {
                                            console.log('📈 Progress update:', parsedData);
                                            onProgress(parsedData);
                                        }
                                    } catch (parseError) {
                                        console.warn('⚠️ Failed to parse SSE data:', parseError);
                                    }
                                }
                            }
                        }
                    } catch (error) {
                        console.error('❌ Stream processing error:', error);
                        reject(error);
                    }
                };

                processStream();
            });
        } else {
            // Fallback: Handle as regular JSON response
            const jsonData = await response.json();
            
            if (onProgress) {
                onProgress({ step: 'processing', message: 'Analyzing pasted content...', progress: 30 });
                await new Promise(resolve => setTimeout(resolve, 500));
                
                onProgress({ step: 'generating', message: 'Extracting recipe data...', progress: 70 });
                await new Promise(resolve => setTimeout(resolve, 500));
                
                onProgress({ step: 'complete', message: 'Recipe transfer complete!', progress: 100 });
            }
            
            return {
                step: 'result',
                recipe: jsonData
            };
        }
    } catch (error) {
        console.error('❌ Error generating recipe from text:', error.message);
        throw error;
    }
};

/**
 * Generate AI analysis for a recipe (from stack) including flavor profile, difficulty, and user compatibility
 * @param {string} recipeId - ID of the recipe to analyze
 * @param {string} token - Authorization token (optional, for personalized analysis)
 * @returns {Promise<Object>} - AI analysis data
 */
export const generateRecipeAnalysis = async (recipeId, token = null) => {
    try {
        const headers = {
            'Content-Type': 'application/json',
        };
        
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }
        
        const response = await api.post(`/recipes/${recipeId}/analyze`, {}, {
            headers,
        });
        
        return response.data;
    } catch (error) {
        console.error('❌ Error generating recipe analysis:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Generate personalized recipe summary based on user preferences and dietary restrictions (from stack)
 * @param {string} recipeId - ID of the recipe to summarize
 * @param {string} token - Authorization token (required for personalized summary)
 * @returns {Promise<Object>} - Personalized summary data
 */
export const generatePersonalizedSummary = async (recipeId, token) => {
    try {
        const response = await api.post(`/recipes/${recipeId}/personalized-summary`, {}, {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
        });
        
        return response.data;
    } catch (error) {
        console.error('❌ Error generating personalized summary:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

// ============================================
// KERNELS API FUNCTIONS
// ============================================

/**
 * Get all bases for the authenticated user
 * @param {string} token - Authorization token
 * @returns {Promise<Array>} - Array of user's bases
 */
export const getAllBases = async (token = null) => {
    try {
        const headers = {};
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }
        
        const response = await api.get('/bases', { headers });
        return response.data;
    } catch (error) {
        console.error('❌ Error fetching bases:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Get a base by ID
 * @param {string} id - ID of the base to fetch
 * @param {string} token - Authorization token
 * @param {boolean} includeImages - Whether to include images with base (optional)
 * @returns {Promise<Object>} - Base data
 */
export const getBaseById = async (id, token = null, includeImages = false) => {
    try {
        const headers = {};
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }
        
        const queryParams = includeImages ? '?includeImages=true' : '';
        const response = await api.get(`/bases/${id}${queryParams}`, { headers });
        return response.data;
    } catch (error) {
        console.error('❌ Error fetching base by ID:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Create a new base
 * @param {Object} baseData - Base data (title, slots)
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Created base data
 */
export const createBase = async (baseData, token) => {
    try {
        const response = await api.post('/bases', baseData, {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error creating base:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Update an existing base
 * @param {string} id - ID of the base to update
 * @param {Object} baseData - Updated base data
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Updated base data
 */
export const updateBase = async (id, baseData, token) => {
    try {
        const response = await api.put(`/bases/${id}`, baseData, {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error updating base:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Delete a base
 * @param {string} id - ID of the base to delete
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Deletion confirmation
 */
export const deleteBase = async (id, token) => {
    try {
        const response = await api.delete(`/bases/${id}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error deleting base:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Add images to an existing base
 * @param {string} baseId - ID of the base to add images to
 * @param {Array<string>} imageUrls - Array of image URLs to add
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Confirmation message
 */
export const addBaseImages = async (baseId, imageUrls, token) => {
    try {
        const response = await api.post(`/bases/${baseId}/images`, 
            { imageUrls }, 
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            }
        );
        return response.data;
    } catch (error) {
        console.error('❌ Error adding images to base:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Get AI recipe suggestion from a base and ingredients
 * @param {string} baseId - ID of the base to use as template
 * @param {Array} ingredients - Array of available ingredients
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Suggested recipe data
 */
export const suggestRecipeFromBase = async (baseId, ingredients, token) => {
    try {
        const response = await api.post(`/bases/${baseId}/suggest-recipe`, 
            { ingredients }, 
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            }
        );
        return response.data;
    } catch (error) {
        console.error('❌ Error suggesting recipe from base:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

// ============================================
// DRAFT API FUNCTIONS
// ============================================

/**
 * Generate a draft recipe from base and slots
 * @param {string} baseId - ID of the base to use
 * @param {Object} slots - User-filled slot values
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Generated draft recipe data
 */
export const generateDraftRecipe = async (baseId, slots, token) => {
    try {
        const response = await api.post('/drafts/generate', { baseId, slots }, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error generating draft recipe:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Get a draft recipe by ID
 * @param {string} draftId - ID of the draft recipe
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Draft recipe data with steps
 */
export const getDraftRecipe = async (draftId, token) => {
    try {
        const response = await api.get(`/drafts/${draftId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error fetching draft recipe:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Save a draft recipe to stack
 * @param {string} draftId - ID of the draft recipe
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Stack entry data
 */
export const saveDraftToStack = async (draftId, token) => {
    try {
        const response = await api.post(`/drafts/${draftId}/save-to-stack`, {}, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error saving draft to stack:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

// ============================================
// STACK API FUNCTIONS (Renamed from Recipes)
// ============================================

/**
 * Create a new stack entry
 * @param {FormData} data - FormData object containing stack fields and images
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Created stack entry data
 */
export const createStackEntry = async (data, token) => {
    try {
        const response = await apiClient.post('/recipes', data, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error creating stack entry:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Update an existing stack entry
 * @param {string} id - ID of the stack entry to update
 * @param {FormData} data - FormData object containing updated fields and images
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Updated stack entry data
 */
export const updateStackEntry = async (id, data, token) => {
    try {
        const response = await apiClient.put(`/recipes/${id}`, data, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error updating stack entry:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Fork a stack entry
 * @param {string} id - ID of the stack entry to fork
 * @param {FormData} data - FormData object containing fork details and images
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Forked stack entry data
 */
export const forkStackEntry = async (id, data, token) => {
    try {
        const response = await apiClient.post(`/recipes/${id}/fork`, data, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error forking stack entry:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Get a stack entry by ID
 * @param {string} id - ID of the stack entry to fetch
 * @returns {Promise<Object>} - Stack entry data
 */
export const getStackEntryById = async (id) => {
    try {
        const response = await api.get(`/recipes/${id}`);
        return response.data;
    } catch (error) {
        console.error('❌ Error fetching stack entry by ID:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Get all stack entries with base relationships
 * @returns {Promise<Array>} - Array of all stack entries
 */
export const getAllStackEntries = async () => {
    try {
        const response = await api.get('/recipes');
        // Convert Firebase Storage URLs to proxy URLs for better accessibility
        const data = response.data.map(recipe => ({
            ...recipe,
            images: convertToProxyUrls(recipe.images)
        }));
        return data;
    } catch (error) {
        console.error('❌ Error fetching all stack entries:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Search recipes by ingredients
 * @param {Array} ingredients - Array of ingredient objects with name, amount, unit
 * @param {boolean} includeUserRecipes - Whether to include user's own recipes
 * @returns {Promise<Object>} - Object with exactMatches and partialMatches arrays
 */
export const searchRecipesByIngredients = async (ingredients, includeUserRecipes = true) => {
    try {
        const user = await import('../firebase/firebaseConfig').then(m => m.auth.currentUser);
        if (!user) {
            throw new Error('No authenticated user found');
        }

        const token = await user.getIdToken();
        const response = await api.post('/recipes/search-by-ingredients', 
            { 
                ingredients,
                includeUserRecipes 
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );

        // Convert image URLs to proxy URLs
        const exactMatches = (response.data.exactMatches || []).map(recipe => ({
            ...recipe,
            images: convertToProxyUrls(recipe.images)
        }));
        
        const partialMatches = (response.data.partialMatches || []).map(recipe => ({
            ...recipe,
            images: convertToProxyUrls(recipe.images)
        }));

        return {
            exactMatches,
            partialMatches
        };
    } catch (error) {
        console.error('❌ Error searching recipes by ingredients:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Get all stack entries for a specific user
 * @param {string} userId - ID of the user
 * @param {string} token - Authorization token
 * @returns {Promise<Array>} - Array of user's stack entries
 */
export const getUserStackEntries = async (userId, token) => {
    try {
        const response = await api.get(`/users/${userId}/recipes`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error fetching user stack entries:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Delete a stack entry
 * @param {string} id - ID of the stack entry to delete
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Deletion confirmation
 */
export const deleteStackEntry = async (id, token) => {
    try {
        const response = await apiClient.delete(`/recipes/${id}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error deleting stack entry:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Generate AI analysis for a stack entry including flavor profile, difficulty, and user compatibility
 * @param {string} stackId - ID of the stack entry to analyze
 * @param {string} token - Authorization token (optional, for personalized analysis)
 * @returns {Promise<Object>} - AI analysis data
 */
export const generateStackAnalysis = async (stackId, token = null) => {
    try {
        const headers = {
            'Content-Type': 'application/json',
        };
        
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }
        
        const response = await api.post(`/recipes/${stackId}/analyze`, {}, {
            headers,
        });
        
        return response.data;
    } catch (error) {
        console.error('❌ Error generating stack analysis:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Generate personalized stack summary based on user preferences and dietary restrictions
 * @param {string} stackId - ID of the stack entry to summarize
 * @param {string} token - Authorization token (required for personalized summary)
 * @returns {Promise<Object>} - Personalized summary data
 */
export const generatePersonalizedStackSummary = async (stackId, token) => {
    try {
        const response = await api.post(`/recipes/${stackId}/personalized-summary`, {}, {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
        });
        
        return response.data;
    } catch (error) {
        console.error('❌ Error generating personalized stack summary:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Get all stack entries (alias for getAllStackEntries for consistency)
 * @returns {Promise<Array>} - Array of all stack entries
 */
export const getStack = async () => {
    try {
        const response = await api.get('/recipes');
        return response.data;
    } catch (error) {
        console.error('❌ Error fetching stack:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Get all bases (public bases + user's private bases if authenticated)
 * @param {string} token - Authorization token (optional)
 * @param {boolean} includeImages - Whether to include images with bases (optional)
 * @returns {Promise<Array>} - Array of bases
 */
export const getBases = async (token = null, includeImages = false) => {
    try {
        const headers = {};
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }
        
        const queryParams = includeImages ? '?includeImages=true' : '';
        const response = await api.get(`/bases${queryParams}`, { headers });
        return response.data;
    } catch (error) {
        console.error('❌ Error fetching bases:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

// ============================================
// COOKING SESSIONS API FUNCTIONS
// ============================================

/**
 * Get all cooking sessions for the authenticated user
 * @param {string} token - Authorization token
 * @returns {Promise<Array>} - Array of cooking sessions
 */
export const getCookingSessions = async (token, forceRefresh = false) => {
    try {
        const url = forceRefresh ? `/sessions?t=${Date.now()}` : '/sessions';
        const response = await api.get(url, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error fetching cooking sessions:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Get a specific cooking session by ID
 * @param {string} sessionId - ID of the cooking session
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Cooking session data
 */
export const getCookingSessionById = async (sessionId, token) => {
    try {
        const response = await api.get(`/sessions/${sessionId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error fetching cooking session:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Create a new cooking session
 * @param {Object} sessionData - Cooking session data
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Created cooking session data
 */
export const createCookingSession = async (sessionData, token) => {
    try {
        const response = await api.post('/sessions', sessionData, {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error creating cooking session:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Mark a cooking session as completed
 * @param {string} sessionId - ID of the cooking session
 * @param {string} token - Authorization token
 * @param {number} elapsedTime - Total elapsed time in seconds
 * @returns {Promise<Object>} - Updated cooking session data
 */
export const completeCookingSession = async (sessionId, token, elapsedTime) => {
    try {
        const response = await api.put(`/sessions/${sessionId}/complete`, {
            elapsed_time: elapsedTime
        }, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error completing cooking session:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Delete a cooking session
 * @param {string} sessionId - ID of the cooking session
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Deletion confirmation
 */
export const deleteCookingSession = async (sessionId, token) => {
    try {
        const response = await api.delete(`/sessions/${sessionId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error deleting cooking session:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Pause a cooking session
 * @param {string} sessionId - ID of the cooking session
 * @param {number} currentStep - Current step index
 * @param {number} timeRemaining - Remaining time in seconds
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Updated cooking session data
 */
export const pauseCookingSession = async (sessionId, currentStep, timeRemaining, token) => {
    try {
        const response = await api.put(`/sessions/${sessionId}/pause`, {
            current_step: currentStep,
            time_remaining: timeRemaining
        }, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error pausing cooking session:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Resume a cooking session
 * @param {string} sessionId - ID of the cooking session
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Updated cooking session data
 */
export const resumeCookingSession = async (sessionId, token) => {
    try {
        const response = await api.put(`/sessions/${sessionId}/resume`, {}, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error resuming cooking session:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Update cooking session with reflection data
 * @param {string} sessionId - ID of the cooking session
 * @param {Object} reflectionData - Reflection data to save
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Updated cooking session data
 */
export const updateSessionReflection = async (sessionId, reflectionData, token) => {
    try {
        const response = await api.put(`/sessions/${sessionId}/reflection`, reflectionData, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error updating session reflection:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};



// ============================================
// COOK NOW API FUNCTIONS
// ============================================

/**
 * Generate recipe variations with different ingredient combinations before creating a Cook Now session
 * @param {string} baseId - ID of the base to use
 * @param {string} goal - User's meal goal description
 * @param {string} pantry - Available ingredients (comma-separated)
 * @param {number} timeMins - Time budget in minutes
 * @param {string} token - Authorization token
 * @param {Array} selectedFlavorThemes - Array of selected flavor theme names
 * @returns {Promise<Object>} - Object containing array of recipe variations
 */
export const generateRecipeVariations = async (baseId, goal, pantry, timeMins, token, selectedFlavorThemes = []) => {
    try {
        const response = await api.post('/cook/variations', {
            baseId,
            goal,
            pantry,
            timeMins,
            selectedFlavorThemes
        }, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error generating recipe variations:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Start a Cook Now session - generate recipe and create live session
 * @param {string} baseId - ID of the base to use
 * @param {string} goal - User's meal goal description
 * @param {string} pantry - Available ingredients (comma-separated)
 * @param {number} timeMins - Time budget in minutes
 * @param {string} token - Authorization token
 * @param {Object} selectedVariation - Optional user-selected recipe variation
 * @returns {Promise<Object>} - Draft recipe data with ID for live session
 */
export const cookNow = async (baseId, goal, pantry, timeMins, token, selectedVariation = null) => {
    try {
        const response = await api.post('/cook', {
            baseId,
            goal,
            pantry,
            timeMins,
            selectedVariation
        }, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error starting cook session:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Create a cooking session from a recipe
 * @param {string} recipeId - ID of the recipe to cook
 * @param {string} token - Authorization token
 * @param {string} ingredients - Optional ingredients (comma-separated)
 * @param {string} instructions - Optional instructions
 * @returns {Promise<Object>} - Created cooking session data
 */
export const createCookingSessionFromRecipe = async (recipeId, token, ingredients = null, instructions = null) => {
    try {
        const payload = { recipe_id: recipeId };
        if (ingredients) payload.ingredients = ingredients;
        if (instructions) payload.instructions = instructions;
        const response = await api.post('/sessions/from-recipe', payload, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        logDebug('[api.createCookingSessionFromRecipe]', 'Session created from recipe', { 
            sessionId: response.data?.id, 
            recipeId,
            hasModifications: !!(ingredients || instructions)
        });
        return response.data;
    } catch (error) {
        // Handle 409 Conflict - an open session already exists
        if (error.response?.status === 409 && error.response?.data?.code === 'OPEN_SESSION_EXISTS') {
            logEvent('session.create.conflict', {
                sessionId: error.response.data.session_id,
                recipeId: recipeId
            });
            logDebug('[api.createCookingSessionFromRecipe]', '409: Resuming existing session', error.response.data.session_id);
            
            // Show a toast notification to the user
            if (typeof window !== 'undefined' && window.showToast) {
                window.showToast('Resuming your existing session', 'info');
            }
            
            // Return the existing session data (treat 409 as success)
            return error.response.data.session || {
                id: error.response.data.session_id,
                recipe_id: error.response.data.recipe_id,
                ...error.response.data
            };
        }
        
        // For other errors, throw as usual
        logError('[api.createCookingSessionFromRecipe]', 'Error creating cooking session from recipe', error.response?.data || error);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Get cooking sessions for a specific recipe
 * @param {string} recipeId - ID of the recipe
 * @param {string} token - Authorization token
 * @returns {Promise<Array>} - Array of cooking sessions for the recipe
 */
export const getRecipeCookingSessions = async (recipeId, token) => {
    try {
        const response = await api.get(`/sessions/recipe/${recipeId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error fetching recipe cooking sessions:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Upload a single image file and return its URL
 * @param {File} file - The image file to upload
 * @param {string} token - Authorization token
 * @returns {Promise<string>} - The uploaded image URL
 */
export const uploadImage = async (file, token) => {
    const formData = new FormData();
    formData.append('image', file);

    const response = await apiClient.post('/upload', formData, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
    return response.data.url;
};

/**
 * Generate ingredient recommendations for a specific base
 * @param {string} baseId - ID of the base to use
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Object containing ingredient recommendations
 */
export const generateBaseRecommendations = async (baseId, token) => {
    try {
        const response = await api.post('/cook/recommendations', {
            baseId
        }, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error generating base recommendations:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

// ============================================
// ADMIN API FUNCTIONS
// ============================================

/**
 * Get admin dashboard statistics
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Admin statistics data
 */
export const getAdminStats = async (token) => {
    try {
        const response = await api.get('/admin/stats', {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error fetching admin stats:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Get all users (admin only)
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Users data with users array
 */
export const getAdminUsers = async (token) => {
    try {
        const response = await api.get('/admin/users', {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error fetching admin users:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Update user admin status (admin only)
 * @param {string} userId - ID of the user to update
 * @param {Object} userData - User data to update (e.g., { is_admin: true })
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Updated user data
 */
export const updateAdminUser = async (userId, userData, token) => {
    try {
        const response = await api.put(`/admin/users/${userId}`, userData, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error updating admin user:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Create a new user (admin only)
 * @param {Object} userData - User data to create
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Created user data
 */
export const createAdminUser = async (userData, token) => {
    try {
        const response = await api.post('/admin/users', userData, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error creating admin user:', error.response?.data?.message || error.message);
        // Ensure we always throw an object with either message or error property
        const errorData = error.response?.data || { message: error.message };
        if (!errorData.message && !errorData.error) {
            errorData.message = error.message || 'Unknown error occurred';
        }
        throw errorData;
    }
};

/**
 * Delete a user (admin only)
 * @param {string} userId - User ID to delete
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Deletion result
 */
export const deleteAdminUser = async (userId, token) => {
    try {
        const response = await api.delete(`/admin/users/${userId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error deleting admin user:', error.response?.data?.message || error.message);
        // Ensure we always throw an object with either message or error property
        const errorData = error.response?.data || { message: error.message };
        if (!errorData.message && !errorData.error) {
            errorData.message = error.message || 'Unknown error occurred';
        }
        throw errorData;
    }
};

/**
 * Toggle admin status for a user (admin only)
 * @param {string} userId - User ID to toggle
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Updated user data
 */
export const toggleAdminStatus = async (userId, token) => {
    try {
        const response = await api.put(`/admin/users/${userId}/toggle-admin`, {}, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error toggling admin status:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Toggle beta status for a user (admin only)
 * @param {string} userId - User ID to toggle
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Updated user data
 */
export const toggleBetaStatus = async (userId, token) => {
    try {
        const response = await api.put(`/admin/users/${userId}/toggle-beta`, {}, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error toggling beta status:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Toggle family status for a user (admin only)
 * @param {string} userId - User ID to toggle
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Updated user data
 */
export const toggleFamilyStatus = async (userId, token) => {
    try {
        const response = await api.put(`/admin/users/${userId}/toggle-family`, {}, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error toggling family status:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Reset walkthrough status for a user (admin only)
 * @param {string} userId - User ID
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Reset result
 */
export const resetWalkthroughStatus = async (userId, token) => {
    try {
        const response = await api.post(`/admin/users/${userId}/reset-walkthrough`, {}, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error resetting walkthrough status:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

export const trackLineageWalkthroughComplete = async (token) => {
    try {
        const response = await api.post('/users/lineage-walkthrough-complete', {}, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error tracking lineage walkthrough completion:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

export const trackLiveSessionWalkthroughComplete = async (token) => {
    try {
        const response = await api.post('/users/live-session-walkthrough-complete', {}, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error tracking live session walkthrough completion:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

export const resetLineageWalkthrough = async (userId, token) => {
    try {
        const response = await api.post(`/admin/users/${userId}/reset-lineage-walkthrough`, {}, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error resetting lineage walkthrough:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

export const resetLiveSessionWalkthrough = async (userId, token) => {
    try {
        const response = await api.post(`/admin/users/${userId}/reset-live-session-walkthrough`, {}, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error resetting live session walkthrough:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Create a new base (admin only)
 * @param {Object} baseData - Base data to create
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Created base data
 */
export const createAdminBase = async (baseData, token) => {
    try {
        const response = await api.post('/admin/bases', baseData, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error creating admin base:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Create a new recipe (admin only)
 * @param {Object} recipeData - Recipe data to create
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Created recipe data
 */
export const createAdminRecipe = async (recipeData, token) => {
    try {
        const response = await api.post('/admin/recipes', recipeData, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error creating admin recipe:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Update a base (admin only)
 * @param {string} id - ID of the base to update
 * @param {Object} baseData - Updated base data
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Updated base data
 */
export const updateAdminBase = async (id, baseData, token) => {
    try {
        const response = await api.put(`/admin/bases/${id}`, baseData, {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error updating admin base:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Get enhanced admin dashboard stats with detailed breakdowns
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Enhanced admin stats
 */
export const getEnhancedAdminStats = async (token) => {
    try {
        const response = await api.get('/admin/stats/enhanced', {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error fetching enhanced admin stats:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Get detailed analytics for a specific user
 * @param {string} userId - ID of the user
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} User analytics data
 */
export const getUserAnalytics = async (userId, token) => {
    try {
        if (!token) {
            throw new Error('No authentication token provided');
        }
        
        const response = await api.get(`/admin/users/${userId}/analytics`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error fetching user analytics:', error.response?.data?.message || error.message);
        
        // Handle specific authentication errors
        if (error.response?.status === 401) {
            throw { message: 'Authentication failed. Please log in again.' };
        }
        
        throw error.response?.data || { message: error.message };
    }
};

// ================================
// COLLECTIONS API
// ================================

/**
 * Get all collections for the authenticated user
 * @param {string} token - Authorization token
 * @returns {Promise<Array>} Array of collections
 */
export const getCollections = async (token) => {
    try {
        const response = await api.get('/collections', {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error fetching collections:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Create a new collection
 * @param {Object} collectionData - Collection data
 * @param {string} collectionData.name - Collection name
 * @param {string} [collectionData.description] - Collection description
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Created collection
 */
export const createCollection = async (collectionData, token) => {
    try {
        const response = await api.post('/collections', collectionData, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error creating collection:', error.response?.data?.message || error.message);
        
        // Handle specific error cases
        if (error.response?.status === 409) {
            throw { message: 'A collection with this name already exists' };
        }
        
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Update a collection
 * @param {number} id - Collection ID
 * @param {Object} collectionData - Updated collection data
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Updated collection
 */
export const updateCollection = async (id, collectionData, token) => {
    try {
        const response = await api.put(`/collections/${id}`, collectionData, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error updating collection:', error.response?.data?.message || error.message);
        
        // Handle specific error cases
        if (error.response?.status === 409) {
            throw { message: 'A collection with this name already exists' };
        }
        
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Delete a collection
 * @param {number} id - Collection ID
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Success message
 */
export const deleteCollection = async (id, token) => {
    try {
        const response = await api.delete(`/collections/${id}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error deleting collection:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Add a recipe to a collection
 * @param {number} collectionId - Collection ID
 * @param {number} recipeId - Recipe ID
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Updated recipe
 */
export const addRecipeToCollection = async (collectionId, recipeId, token) => {
    try {
        const response = await api.post(`/collections/${collectionId}/recipes`, { recipeId }, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error adding recipe to collection:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Remove a recipe from its collection
 * @param {number} recipeId - Recipe ID
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Updated recipe
 */
export const removeRecipeFromCollection = async (recipeId, token) => {
    try {
        const response = await api.delete(`/collections/0/recipes/${recipeId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error removing recipe from collection:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

// Note: Legacy recipe functions are now updated to use stack endpoints above

// ===== MERGE PROPOSALS API =====

/**
 * Get merge proposals for a recipe
 * @param {number} recipeId - Recipe ID
 * @param {string} type - Type of proposals ('sent', 'received', or 'all')
 * @param {string} token - Authorization token
 * @returns {Promise<Array>} Array of merge proposals
 */
export const getMergeProposals = async (recipeId, type = 'all', token) => {
    try {
        const response = await api.get(`/merge-proposals?recipe_id=${recipeId}&type=${type}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error fetching merge proposals:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Get a specific merge proposal
 * @param {number} proposalId - Proposal ID
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Merge proposal data
 */
export const getMergeProposal = async (proposalId, token) => {
    try {
        const response = await api.get(`/merge-proposals/${proposalId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error fetching merge proposal:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Create a new merge proposal
 * @param {Object} proposalData - Proposal data
 * @param {number} proposalData.source_recipe_id - Source recipe ID
 * @param {number} proposalData.target_recipe_id - Target recipe ID
 * @param {string} proposalData.title - Proposal title
 * @param {string} proposalData.notes - Proposal notes (optional)
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Created merge proposal
 */
export const createMergeProposal = async (proposalData, token) => {
    try {
        const response = await api.post('/merge-proposals', proposalData, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error creating merge proposal:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Update Gold Standard for a recipe
 * @param {number} recipeId - Recipe ID to update as Gold Standard
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Update result
 */
export const updateGoldStandard = async (recipeId, token) => {
    try {
        const response = await api.post(`/recipes/${recipeId}/update-gold-standard`, {}, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error updating Gold Standard:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Update a merge proposal status
 * @param {number} proposalId - Proposal ID
 * @param {Object} updateData - Update data
 * @param {string} updateData.status - New status ('approved' or 'rejected')
 * @param {string} updateData.review_notes - Review notes (optional)
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Updated merge proposal
 */
export const updateMergeProposal = async (proposalId, updateData, token) => {
    try {
        const response = await api.put(`/merge-proposals/${proposalId}`, updateData, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error updating merge proposal:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Get potential merge targets for a recipe (same chain family)
 * @param {number} recipeId - Recipe ID
 * @param {string} token - Authorization token
 * @returns {Promise<Array>} Array of potential merge targets
 */
export const getMergeTargets = async (recipeId, token) => {
    try {
        const response = await api.get(`/merge-proposals/recipe/${recipeId}/lineage`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error fetching merge targets:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

// ===== NOTIFICATIONS API =====

/**
 * Get notifications for the current user
 * @param {Object} options - Query options
 * @param {number} options.limit - Number of notifications to fetch
 * @param {number} options.offset - Offset for pagination
 * @param {boolean} options.unread_only - Only fetch unread notifications
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Notifications and unread count
 */
export const getNotifications = async (options = {}, token) => {
    try {
        const params = new URLSearchParams();
        if (options.limit) params.append('limit', options.limit);
        if (options.offset) params.append('offset', options.offset);
        if (options.unread_only) params.append('unread_only', options.unread_only);
        
        const response = await api.get(`/notifications?${params.toString()}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error fetching notifications:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Mark a notification as read
 * @param {number} notificationId - Notification ID
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Updated notification
 */
export const markNotificationAsRead = async (notificationId, token) => {
    try {
        const response = await api.put(`/notifications/${notificationId}/read`, {}, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error marking notification as read:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Mark all notifications as read for the current user
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Success message and count
 */
export const markAllNotificationsAsRead = async (token) => {
    try {
        const response = await api.put('/notifications/read-all', {}, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error marking all notifications as read:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Delete a notification
 * @param {number} notificationId - Notification ID
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Success message
 */
export const deleteNotification = async (notificationId, token) => {
    try {
        const response = await api.delete(`/notifications/${notificationId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error deleting notification:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

// ===== RECIPE UPDATES API =====

/**
 * Get impact analysis for updating a recipe
 * @param {number} recipeId - Recipe ID
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Impact analysis data
 */
export const getRecipeUpdateImpact = async (recipeId, token) => {
    try {
        const response = await api.get(`/recipe-updates/${recipeId}/impact-analysis`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error fetching recipe update impact:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Notify fork owners about upcoming recipe changes
 * @param {number} recipeId - Recipe ID
 * @param {Object} notificationData - Notification data
 * @param {string} notificationData.message - Notification message
 * @param {string} notificationData.planned_changes - Planned changes (optional)
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Success message and count
 */
export const notifyForkOwners = async (recipeId, notificationData, token) => {
    try {
        const response = await api.post(`/recipe-updates/${recipeId}/notify-forks`, notificationData, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error notifying fork owners:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

// ===== FRIENDS API =====

/**
 * Search for users by username or email
 * @param {string} query - Search query
 * @param {string} token - Authorization token
 * @returns {Promise<Array>} Array of matching users
 */
export const searchUsers = async (query, token) => {
    try {
        const response = await api.get(`/friends/search?query=${encodeURIComponent(query)}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error searching users:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Send a friend request
 * @param {string} receiverId - User ID to send request to
 * @param {string} message - Optional message
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Friend request data
 */
export const sendFriendRequest = async (receiverId, message, token) => {
    try {
        const response = await api.post('/friends/request', {
            receiver_id: receiverId,
            message,
        }, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error sending friend request:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Get friend requests
 * @param {string} type - 'all', 'received', or 'sent'
 * @param {string} token - Authorization token
 * @returns {Promise<Array>} Array of friend requests
 */
export const getFriendRequests = async (type = 'all', token) => {
    try {
        const response = await api.get(`/friends/requests?type=${type}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error fetching friend requests:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Accept a friend request
 * @param {number} requestId - Friend request ID
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Success message
 */
export const acceptFriendRequest = async (requestId, token) => {
    try {
        const response = await api.put(`/friends/request/${requestId}/accept`, {}, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error accepting friend request:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Reject a friend request
 * @param {number} requestId - Friend request ID
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Success message
 */
export const rejectFriendRequest = async (requestId, token) => {
    try {
        const response = await api.put(`/friends/request/${requestId}/reject`, {}, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error rejecting friend request:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Get list of friends
 * @param {string} token - Authorization token
 * @returns {Promise<Array>} Array of friends
 */
export const getFriends = async (token) => {
    try {
        const response = await api.get('/friends', {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error fetching friends:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Remove a friend
 * @param {string} friendId - Friend's user ID
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Success message
 */
export const removeFriend = async (friendId, token) => {
    try {
        const response = await api.delete(`/friends/${friendId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error removing friend:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Get friends' cooking activity
 * @param {Object} options - Query options
 * @param {number} options.limit - Number of activities to fetch
 * @param {number} options.offset - Offset for pagination
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Activities and total count
 */
export const getFriendsActivity = async (options = {}, token) => {
    try {
        const params = new URLSearchParams();
        if (options.limit) params.append('limit', options.limit);
        if (options.offset) params.append('offset', options.offset);
        
        const response = await api.get(`/friends/activity?${params.toString()}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error fetching friends activity:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

// ===== POSTS API =====

/**
 * Create a new post from a cooking session
 * @param {Object} postData - Post data
 * @param {string} postData.session_id - Session ID
 * @param {string} postData.caption - Post caption
 * @param {Object} postData.final_image - Final dish photo
 * @param {Array} postData.progress_photos - Progress photos with captions
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Created post
 */
export const createPost = async (postData, token) => {
    try {
        const response = await api.post('/posts', postData, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error creating post:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Get posts from friends (for The Table page)
 * @param {Object} options - Query options
 * @param {number} options.limit - Number of posts to fetch
 * @param {number} options.offset - Offset for pagination
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Posts and total count
 */
export const getFriendsPosts = async (options = {}, token) => {
    try {
        const params = new URLSearchParams();
        if (options.limit) params.append('limit', options.limit);
        if (options.offset) params.append('offset', options.offset);
        
        const response = await api.get(`/posts/friends?${params.toString()}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error fetching friends posts:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Get posts from a specific user
 * @param {string} userId - User ID
 * @param {Object} options - Query options
 * @param {number} options.limit - Number of posts to fetch
 * @param {number} options.offset - Offset for pagination
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Posts
 */
export const getUserPosts = async (userId, options = {}, token) => {
    try {
        const params = new URLSearchParams();
        if (options.limit) params.append('limit', options.limit);
        if (options.offset) params.append('offset', options.offset);
        
        const response = await api.get(`/posts/user/${userId}?${params.toString()}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error fetching user posts:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Delete a post
 * @param {string} postId - Post ID
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Success message
 */
export const deletePost = async (postId, token) => {
    try {
        const response = await api.delete(`/posts/${postId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error deleting post:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

// ===== KITCHEN STOCK API =====

/**
 * Get user's kitchen stock
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Stock items
 */
export const getKitchenStock = async (token) => {
    try {
        const response = await api.get('/kitchen-stock', {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error getting kitchen stock:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Add a stock item
 * @param {Object} item - Stock item data
 * @param {string} item.ingredient_name - Ingredient name
 * @param {number} item.quantity - Quantity
 * @param {string} item.unit - Unit (cups, oz, count, lbs, etc.)
 * @param {string} item.expiration_date - Optional expiration date (ISO string)
 * @param {number} item.low_stock_threshold - Optional low stock threshold
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Created stock item
 */
export const addStockItem = async (item, token) => {
    try {
        const response = await api.post('/kitchen-stock', item, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error adding stock item:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Generate an AI image for an ingredient
 * @param {string} ingredientName - Name of the ingredient
 * @param {string} token - Authorization token
 * @returns {Promise<string>} URL of the generated image
 */
export const generateIngredientImage = async (ingredientName, token) => {
    try {
        const response = await api.post('/kitchen-stock/generate-image', 
            { ingredient_name: ingredientName },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        return response.data.image_url;
    } catch (error) {
        console.error('❌ Error generating ingredient image:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Update a stock item
 * @param {number} id - Stock item ID
 * @param {Object} updates - Fields to update
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Updated stock item
 */
export const updateStockItem = async (id, updates, token) => {
    try {
        const response = await api.put(`/kitchen-stock/${id}`, updates, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error updating stock item:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Delete a stock item
 * @param {number} id - Stock item ID
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Success message
 */
export const deleteStockItem = async (id, token) => {
    try {
        const response = await api.delete(`/kitchen-stock/${id}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error deleting stock item:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Check for low stock items
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Low stock items and notification count
 */
export const checkLowStock = async (token) => {
    try {
        const response = await api.post('/kitchen-stock/check-low-stock', {}, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error checking low stock:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

// ===== GROCERY LISTS API =====

/**
 * Generate a grocery list from selected recipes
 * Enhanced to support servings map and occasion
 * @param {Array<number>} recipeIds - Array of recipe IDs
 * @param {string} token - Authorization token
 * @param {Object} servingsMap - Optional map of recipeId -> servings {1: 4, 2: 6}
 * @param {string} occasionId - Optional occasion ID
 * @returns {Promise<Object>} Generated grocery list
 */
export const generateGroceryList = async (recipeIds, token, servingsMap = null, occasionId = null) => {
    try {
        const response = await api.post('/grocery-lists/generate', { 
            recipeIds, 
            servingsMap, 
            occasionId 
        }, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error generating grocery list:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Generate a grocery list from an occasion
 * @param {string} occasionId - Occasion ID
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Generated grocery list with stock check data
 */
export const generateGroceryListFromOccasion = async (occasionId, token) => {
    try {
        const response = await api.post(`/grocery-lists/generate-from-occasion/${occasionId}`, {}, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error generating grocery list from occasion:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Check stock for a grocery list occasion
 * @param {string} occasionId - Occasion ID
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Stock check data
 */
export const checkStockForGroceryList = async (occasionId, token) => {
    try {
        // This will be implemented when we have the stock check endpoint
        // For now, generate the list which includes stock check data
        const groceryList = await generateGroceryListFromOccasion(occasionId, token);
        return { stockCheckData: groceryList.stockCheckData || [] };
    } catch (error) {
        console.error('❌ Error checking stock for grocery list:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Get stock check data for an existing grocery list
 * @param {number} listId - List ID
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Stock check data
 */
export const getGroceryListStockCheck = async (listId, token) => {
    try {
        const response = await api.get(`/grocery-lists/${listId}/stock-check`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error getting grocery list stock check:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Get user's saved grocery lists
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Array of grocery lists
 */
export const getGroceryLists = async (token) => {
    try {
        const response = await api.get('/grocery-lists', {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error getting grocery lists:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Get a specific grocery list
 * @param {number} listId - List ID
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Grocery list
 */
export const getGroceryList = async (listId, token) => {
    try {
        const response = await api.get(`/grocery-lists/${listId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error getting grocery list:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Save a grocery list
 * @param {Object} listData - List data
 * @param {string} listData.name - Optional list name
 * @param {Array<number>} listData.recipeIds - Recipe IDs
 * @param {Array} listData.items - Grocery items
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Saved list
 */
export const saveGroceryList = async (listData, token) => {
    try {
        const response = await api.post('/grocery-lists', listData, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error saving grocery list:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Update a grocery list item (check/uncheck)
 * @param {number} listId - List ID
 * @param {number} itemIndex - Item index in the items array
 * @param {boolean} checked - Checked status
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Updated list
 */
export const updateGroceryListItem = async (listId, itemIndex, checked, token) => {
    try {
        const response = await api.put(`/grocery-lists/${listId}/items/${itemIndex}`, { checked }, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error updating grocery list item:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Remove a grocery list item (when checked)
 * @param {number} listId - List ID
 * @param {number} itemIndex - Item index in the items array
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Updated list
 */
export const removeGroceryListItem = async (listId, itemIndex, token) => {
    try {
        const response = await api.delete(`/grocery-lists/${listId}/items/${itemIndex}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error removing grocery list item:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Add a recipe to grocery list from recipe details
 * @param {number} recipeId - Recipe ID
 * @param {number} servings - Optional servings (defaults to recipe default)
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Generated grocery list with pantry cross-referencing
 */
export const addRecipeToGroceryList = async (recipeId, servings = null, token) => {
    try {
        const response = await api.post('/grocery-lists/add-from-recipe', { 
            recipeId, 
            servings 
        }, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error adding recipe to grocery list:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Upload a receipt image for OCR processing
 * @param {File} imageFile - Receipt image file
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Processed receipt with extracted items
 */
export const uploadReceipt = async (imageFile, token) => {
    try {
        const formData = new FormData();
        formData.append('receipt', imageFile);
        
        const response = await apiClient.post('/kitchen-stock/upload-receipt', formData, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error uploading receipt:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Get user's receipts
 * @param {string} token - Authorization token
 * @param {number} limit - Optional limit (default 50)
 * @returns {Promise<Object>} Array of receipts
 */
export const getReceipts = async (token, limit = 50) => {
    try {
        const response = await api.get('/kitchen-stock/receipts', {
            params: { limit },
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error getting receipts:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Update extracted items for a receipt (manual correction)
 * @param {number} receiptId - Receipt ID
 * @param {Array} items - Updated items array
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Updated receipt
 */
export const updateReceiptItems = async (receiptId, items, token) => {
    try {
        const response = await api.put(`/kitchen-stock/receipts/${receiptId}`, { items }, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error updating receipt items:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Delete a receipt
 * @param {number} receiptId - Receipt ID
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Success message
 */
export const deleteReceipt = async (receiptId, token) => {
    try {
        const response = await api.delete(`/kitchen-stock/receipts/${receiptId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error deleting receipt:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Get pantry recommendations for ingredients
 * @param {Array<Object>} ingredients - Array of {ingredient_name, quantity, unit}
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Recommendation data
 */
export const getPantryRecommendations = async (ingredients, token) => {
    try {
        const response = await api.post('/grocery-lists/pantry-recommendations', { ingredients }, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error getting pantry recommendations:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Get or create the active grocery list
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Active grocery list
 */
export const getActiveGroceryList = async (token) => {
    try {
        const response = await api.get('/grocery-lists/active', {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error getting active grocery list:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Delete a recipe section from a grocery list
 * @param {number} listId - List ID
 * @param {number} recipeId - Recipe ID to remove
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Updated list
 */
export const deleteRecipeSection = async (listId, recipeId, token) => {
    try {
        const response = await api.delete(`/grocery-lists/${listId}/recipes/${recipeId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error deleting recipe section:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Add an individual item to a grocery list
 * @param {number} listId - List ID
 * @param {Object} item - Item data { ingredient_name, quantity_needed, quantity_to_buy, unit, recipe_sources }
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Updated list
 */
export const addIndividualItem = async (listId, item, token) => {
    try {
        const response = await api.post(`/grocery-lists/${listId}/items`, item, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error adding individual item:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Delete a grocery list
 * @param {number} listId - List ID
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Success message
 */
export const deleteGroceryList = async (listId, token) => {
    try {
        const response = await api.delete(`/grocery-lists/${listId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error deleting grocery list:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Match products for a grocery list
 * @param {number} listId - List ID
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Product matches
 */
export const matchGroceryListProducts = async (listId, token) => {
    try {
        const response = await api.post(`/grocery-lists/${listId}/match-products`, {}, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error matching products:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Get matched products for a grocery list
 * @param {number} listId - List ID
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Product matches grouped by item index
 */
export const getGroceryListProducts = async (listId, token) => {
    try {
        const response = await api.get(`/grocery-lists/${listId}/products`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error getting products:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Update/select a product match
 * @param {number} listId - List ID
 * @param {number} productId - Product ID
 * @param {Object} data - Update data { is_selected, etc. }
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Updated product
 */
export const updateGroceryListProduct = async (listId, productId, data, token) => {
    try {
        const response = await api.put(`/grocery-lists/${listId}/products/${productId}`, data, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error updating product:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Save user's store preference
 * @param {Object} preference - Preference data { preferred_store, store_location, zip_code }
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Saved preference
 */
export const saveStorePreference = async (preference, token) => {
    try {
        const response = await api.post(`/grocery-lists/user-store-preference`, preference, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error saving store preference:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Get user's store preference
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} User preference
 */
export const getStorePreference = async (token) => {
    try {
        const response = await api.get(`/grocery-lists/user-store-preference`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error getting store preference:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

// ===== OCCASIONS API (Unified) =====

/**
 * Create an occasion
 * @param {Object} data - Occasion data
 * @param {string} data.name - Occasion name
 * @param {string} data.description - Optional description
 * @param {string} data.occasion_type - Type: 'menu_plan', 'grocery_list', 'custom'
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Created occasion
 */
export const createOccasion = async (data, token) => {
    try {
        const response = await api.post('/occasions', data, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error creating occasion:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Get user's occasions, optionally filtered by type
 * @param {string} token - Authorization token
 * @param {string} occasionType - Optional type filter
 * @returns {Promise<Object>} Array of occasions
 */
export const getOccasions = async (token, occasionType = null) => {
    try {
        const url = occasionType ? `/occasions?type=${occasionType}` : '/occasions';
        const response = await api.get(url, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error getting occasions:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Get a specific occasion with recipes
 * @param {string} occasionId - Occasion ID
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Occasion with recipes
 */
export const getOccasion = async (occasionId, token) => {
    try {
        const response = await api.get(`/occasions/${occasionId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error getting occasion:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Update an occasion
 * @param {string} occasionId - Occasion ID
 * @param {Object} data - Update data
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Updated occasion
 */
export const updateOccasion = async (occasionId, data, token) => {
    try {
        const response = await api.put(`/occasions/${occasionId}`, data, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error updating occasion:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Delete an occasion
 * @param {string} occasionId - Occasion ID
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Success message
 */
export const deleteOccasion = async (occasionId, token) => {
    try {
        const response = await api.delete(`/occasions/${occasionId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error deleting occasion:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Add a recipe to an occasion
 * @param {string} occasionId - Occasion ID
 * @param {number} recipeId - Recipe ID
 * @param {number} servings - Number of servings (default: 4)
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Created occasion_recipe
 */
export const addRecipeToOccasion = async (occasionId, recipeId, servings, token) => {
    try {
        const response = await api.post(`/occasions/${occasionId}/recipes`, {
            recipe_id: recipeId,
            servings: servings || 4
        }, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error adding recipe to occasion:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Remove a recipe from an occasion
 * @param {string} occasionId - Occasion ID
 * @param {number} recipeId - Recipe ID
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Success message
 */
export const removeRecipeFromOccasion = async (occasionId, recipeId, token) => {
    try {
        const response = await api.delete(`/occasions/${occasionId}/recipes/${recipeId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error removing recipe from occasion:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Update serving count for a recipe in an occasion
 * @param {string} occasionId - Occasion ID
 * @param {number} recipeId - Recipe ID
 * @param {number} servings - New serving count
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Updated occasion_recipe
 */
export const updateOccasionRecipeServings = async (occasionId, recipeId, servings, token) => {
    try {
        const response = await api.put(`/occasions/${occasionId}/recipes/${recipeId}/servings`, {
            servings
        }, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error updating occasion recipe servings:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Create an occasion from a list of recipes with serving counts
 * @param {Array<number>} recipeIds - Array of recipe IDs
 * @param {Object} servingsMap - Map of recipeId -> servings {1: 4, 2: 6}
 * @param {string} name - Occasion name
 * @param {string} occasionType - Occasion type
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Created occasion with recipes
 */
export const createOccasionFromRecipes = async (recipeIds, servingsMap, name, occasionType, token) => {
    try {
        const response = await api.post('/occasions/from-recipes', {
            recipe_ids: recipeIds,
            servings_map: servingsMap,
            name,
            occasion_type: occasionType
        }, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error creating occasion from recipes:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Delete a menu plan
 * @param {string|number} planId - Menu plan ID
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Success result
 */
export const deleteMenuPlan = async (planId, token) => {
    try {
        const response = await api.delete(`/menu-planning/plans/${planId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error deleting menu plan:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Submit user feedback via Slack webhook (Firebase Function)
 * @param {string} feedback - User feedback text
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} Success result
 */
export const submitFeedback = async (feedback, token) => {
    try {
        // Get Firebase project ID from environment or use default
        const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'forked-prod';
        const region = 'us-central1'; // Default region for Firebase Functions
        
        // Check if Firebase emulator is explicitly enabled via environment variable
        const useEmulator = process.env.REACT_APP_USE_FIREBASE_EMULATOR === 'true';
        
        // Construct Firebase Function URL
        // Use production URL by default (emulator only if explicitly enabled)
        const functionUrl = useEmulator
            ? `http://localhost:5001/${projectId}/${region}/submitFeedback`
            : `https://${region}-${projectId}.cloudfunctions.net/submitFeedback`;
        
        const response = await fetch(functionUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ feedback }),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
            throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('❌ Error submitting feedback:', error.message);
        throw error;
    }
};